import { CheckCircle, Mail, Clock, FileSearch, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CompleteScreenProps {
    appNumber: string;
    summary: {
        fullName: string;
        appliedTerm: string;
        email: string;
        mobile: string;
        degree: string;
        stage: string;
        citizenship: string;
        appliedProgram: string;
    };
    onReturn?: () => void;
    returnLabel?: string;
}

const NEXT_STEPS = [
    {
        icon: Mail,
        title: "Check your email",
        description: "A confirmation has been sent to your registered email address.",
    },
    {
        icon: FileSearch,
        title: "Document review",
        description: "Our admissions team will review your documents within 3–5 business days.",
    },
    {
        icon: Phone,
        title: "Interview (if applicable)",
        description: "You may be contacted to schedule an interview depending on your programme.",
    },
    {
        icon: Clock,
        title: "Decision",
        description: "Final admission decisions are communicated within 2–4 weeks after document review.",
    },
];

export default function CompleteScreen({
    appNumber,
    summary,
    onReturn,
    returnLabel = "Return to Applications",
}: CompleteScreenProps) {
    return (
        <div className="flex flex-col items-center gap-8 py-4 md:py-8 w-full">
            {/* Success icon */}
            <div className="relative flex items-center justify-center">
                <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-emerald-500 flex items-center justify-center shadow-xl shadow-emerald-200">
                    <CheckCircle className="w-10 h-10 md:w-14 md:h-14 text-white" strokeWidth={2} />
                </div>
            </div>

            {/* Heading */}
            <div className="text-center space-y-2 md:space-y-3">
                <h2 className="text-2xl md:text-4xl font-serif font-black text-[#001B44] leading-tight">
                    Application Submitted!
                </h2>
                <p className="text-slate-500 font-medium max-w-sm text-sm md:text-base">
                    Your application is now under review. You can track its progress from your portal.
                </p>
            </div>

            {/* Application number badge */}
            <div className="flex flex-col items-center gap-1.5">
                <p className="text-[9px] font-black uppercase tracking-[0.25em] text-slate-400">
                    Application Number
                </p>
                <div className="flex items-center gap-2 bg-[#001B44] text-white px-5 py-2.5 rounded-2xl shadow-md">
                    <span className="font-black text-base md:text-lg tracking-widest">{appNumber || "—"}</span>
                </div>
                <p className="text-[10px] text-slate-400 font-medium">Keep this number for your records</p>
            </div>

            {/* Summary card */}
            <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-slate-50/60 p-4 md:p-6 space-y-4">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#001B44]" />
                    Submission Summary
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                        { label: "Applicant", value: summary.fullName },
                        { label: "Programme", value: summary.appliedProgram },
                        { label: "Degree", value: summary.degree },
                        { label: "Intake Term", value: summary.appliedTerm },
                        { label: "Email", value: summary.email },
                        { label: "Mobile", value: summary.mobile },
                    ].map((item) => (
                        <div key={item.label} className="space-y-1">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                {item.label}
                            </p>
                            <p className="text-xs font-semibold text-slate-800 bg-white rounded-xl border border-slate-200 px-3 py-2 min-h-[34px] flex items-center">
                                {item.value || <span className="text-slate-300 font-normal italic">—</span>}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* What happens next */}
            <div className="w-full max-w-2xl space-y-3">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    What happens next
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {NEXT_STEPS.map((step, i) => (
                        <div
                            key={i}
                            className="flex items-start gap-3 bg-white rounded-2xl border border-slate-200 p-4"
                        >
                            <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                                <step.icon className="w-4 h-4 text-[#001B44]" />
                            </div>
                            <div className="space-y-0.5">
                                <p className="text-xs font-black text-slate-900">{step.title}</p>
                                <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                                    {step.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* CTA */}
            {onReturn && (
                <Button
                    onClick={onReturn}
                    className="rounded-xl md:rounded-2xl px-8 h-11 md:h-14 font-black bg-[#001B44] hover:bg-[#002b66] text-white text-xs md:text-sm uppercase tracking-[0.1em] transition-all active:scale-95"
                >
                    {returnLabel}
                </Button>
            )}
        </div>
    );
}
